import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { Menu, X, GraduationCap, LogOut, LayoutDashboard, Award, Briefcase, ShieldCheck } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const linkCls = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? "text-ink-900" : "text-ink-600 hover:text-ink-900"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-ink-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-gold-light to-gold-500 flex items-center justify-center shadow-gold">
            <GraduationCap className="w-5 h-5 text-white" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-bold text-ink-900 tracking-tight">E.C.S</div>
            <div className="font-mono text-[9px] text-ink-500 -mt-0.5">Education · Certification · School</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Accueil</NavLink>
          <NavLink to="/categories" className={linkCls} data-testid="nav-categories">Catégories</NavLink>
          <NavLink to="/recrutement" className={linkCls} data-testid="nav-recrutement">Recrutement</NavLink>
          <NavLink to="/faq" className={linkCls} data-testid="nav-faq">FAQ</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              {user.is_admin && (
                <Link to="/admin" className="px-3 py-2 text-sm text-gold-600 hover:text-gold-700 font-medium flex items-center gap-1.5" data-testid="nav-admin">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to="/certificates" className="px-3 py-2 text-sm text-ink-600 hover:text-ink-900 flex items-center gap-1.5" data-testid="nav-certificates">
                <Award className="w-4 h-4" /> Certificats
              </Link>
              <Link to="/dashboard" className="px-3 py-2 text-sm text-ink-600 hover:text-ink-900 flex items-center gap-1.5" data-testid="nav-dashboard">
                <LayoutDashboard className="w-4 h-4" /> Tableau de bord
              </Link>
              <button
                onClick={() => { logout(); nav("/"); }}
                className="ml-1 px-4 py-2 rounded-lg border border-ink-200 text-sm text-ink-700 hover:bg-ink-50 transition inline-flex items-center gap-1.5"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="px-4 py-2 text-sm text-ink-700 hover:text-ink-900 font-medium" data-testid="login-link">Connexion</Link>
              <Link to="/register" className="px-5 py-2 rounded-lg bg-gold-500 text-white font-semibold text-sm hover:bg-gold-600 shadow-gold transition-all" data-testid="register-link">
                Créer un compte
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-ink-900 p-2"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-ink-200 px-4 py-4 space-y-2" data-testid="mobile-menu">
          <NavLink to="/" end className="block py-2 text-ink-700" onClick={() => setOpen(false)}>Accueil</NavLink>
          <NavLink to="/categories" className="block py-2 text-ink-700" onClick={() => setOpen(false)}>Catégories</NavLink>
          <NavLink to="/recrutement" className="block py-2 text-ink-700 flex items-center gap-2" onClick={() => setOpen(false)}>
            <Briefcase className="w-4 h-4" /> Recrutement
          </NavLink>
          <NavLink to="/faq" className="block py-2 text-ink-700" onClick={() => setOpen(false)}>FAQ</NavLink>
          <NavLink to="/contact" className="block py-2 text-ink-700" onClick={() => setOpen(false)}>Contact</NavLink>
          {user ? (
            <>
              <NavLink to="/dashboard" className="block py-2 text-gold-600 font-medium" onClick={() => setOpen(false)}>Tableau de bord</NavLink>
              <NavLink to="/certificates" className="block py-2 text-gold-600 font-medium" onClick={() => setOpen(false)}>Mes certificats</NavLink>
              {user.is_admin && <NavLink to="/admin" className="block py-2 text-gold-600 font-medium" onClick={() => setOpen(false)}>Admin</NavLink>}
              <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="block py-2 text-ink-700 w-full text-left">Déconnexion</button>
            </>
          ) : (
            <>
              <NavLink to="/login" className="block py-2 text-ink-700" onClick={() => setOpen(false)}>Connexion</NavLink>
              <NavLink to="/register" className="block py-2 text-gold-600 font-medium" onClick={() => setOpen(false)}>Créer un compte</NavLink>
            </>
          )}
        </div>
      )}
    </header>
  );
}
